// Base64 encode/decode helpers shared across the converter pages.
// Uses the browser's btoa/atob with UTF-8 safe wrapping so multi-byte
// characters (emoji, accents, non-Latin scripts) round-trip correctly.

export type Base64Variant = 'standard' | 'url-safe' | 'mime';

// Encode UTF-8 text to standard Base64.
function encodeStandard(text: string): string {
  return btoa(unescape(encodeURIComponent(text)));
}

// Decode standard Base64 back to UTF-8 text.
function decodeStandard(base64: string): string {
  return decodeURIComponent(escape(atob(base64)));
}

// Convert standard Base64 to the URL-safe alphabet ("+/" -> "-_") and drop padding.
function toUrlSafe(base64: string): string {
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

// Restore standard Base64 from the URL-safe alphabet and re-pad before decoding.
function fromUrlSafe(input: string): string {
  let base64 = input.replace(/-/g, '+').replace(/_/g, '/');
  const pad = base64.length % 4;
  if (pad) {
    base64 += '='.repeat(4 - pad);
  }
  return base64;
}

// MIME (RFC 2045) wraps encoded output at 76 characters per line, separated by CRLF.
function toMimeLines(base64: string): string {
  return base64.match(/.{1,76}/g)?.join('\r\n') ?? base64;
}

export function encodeBase64(text: string, variant: Base64Variant = 'standard'): string {
  const encoded = encodeStandard(text);
  if (variant === 'url-safe') return toUrlSafe(encoded);
  if (variant === 'mime') return toMimeLines(encoded);
  return encoded;
}

export function decodeBase64(input: string, variant: Base64Variant = 'standard'): string {
  if (variant === 'url-safe') return decodeStandard(fromUrlSafe(input.trim()));
  if (variant === 'mime') return decodeStandard(input.replace(/\s+/g, ''));
  return decodeStandard(input.trim());
}
